import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert, Button, Form, Modal, Spinner,
} from 'react-bootstrap';
import { ImageCropper, useImageCropper } from '../../../dorian-shared/components/ImageCropper/ImageCropper';
import { imageFileToDataURL } from '../../../dorian-shared/components/ImageCropper/imageFileToDataURL';
import { apiErrorMessage, fetchPreviousCovers, uploadCover } from './discoverShelvesApi';

const ACCEPTED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const COVER_ASPECT_RATIO = 438 / 600;

interface CoverDialogProps {
  bookUuid: string;
  onConfirm: (cover: { key: string; url: string }) => void;
  onCancel: () => void;
}

// Modal with a static backdrop, so the rest of the tool is blocked until Confirm or Cancel.
// Confirm uploads the cropped image right away (or reuses a previous cover) and returns its key.
export function CoverDialog(props: CoverDialogProps) {
  const { bookUuid, onConfirm, onCancel } = props;
  const { handleCropperInitialized, getBlob } = useImageCropper();
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [previousCovers, setPreviousCovers] = useState<{ key: string; url: string }[]>([]);
  const [selectedPrevious, setSelectedPrevious] = useState<{ key: string; url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPreviousCovers(bookUuid).then(setPreviousCovers).catch(() => setPreviousCovers([]));
  }, [bookUuid]);

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setError(null);
    setCroppedBlob(null);
    setCroppedPreview(null);
    setSelectedPrevious(null);
    if (!file) {
      return;
    }
    const lowerName = file.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
      setError(`Use one of: ${ACCEPTED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('Image must be 10MB or smaller');
      return;
    }
    imageFileToDataURL(file).then(setImageToCrop);
  };

  const handleCrop = () => getBlob({ croppedImageType: 'image/jpeg', width: 438, height: 600 })
    .then((blob) => {
      if (blob) {
        setCroppedBlob(blob);
        setCroppedPreview(URL.createObjectURL(blob));
      }
    });

  const handleConfirm = () => {
    if (selectedPrevious) {
      onConfirm(selectedPrevious);
      return;
    }
    if (!croppedBlob) {
      return;
    }
    setUploading(true);
    uploadCover(croppedBlob)
      .then(onConfirm)
      .catch((err) => setError(apiErrorMessage(err, 'Failed to upload cover')))
      .finally(() => setUploading(false));
  };

  return (
    <Modal show size="lg" backdrop="static" keyboard={false} onHide={onCancel}>
      <Modal.Header>
        <Modal.Title>Shelf cover</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group>
          <Form.Label>Upload a new cover</Form.Label>
          <Form.Control type="file" accept={ACCEPTED_EXTENSIONS.join(',')} onChange={handleFile} disabled={uploading} />
        </Form.Group>
        {imageToCrop && (
          <>
            <ImageCropper
              src={imageToCrop}
              aspectRatio={COVER_ASPECT_RATIO}
              onInitialized={handleCropperInitialized}
              style={{ maxHeight: 400 }}
            />
            <Button className="mt-2" variant="secondary" size="sm" onClick={handleCrop} disabled={uploading}>
              Crop &amp; preview
            </Button>
          </>
        )}
        {croppedPreview && (
          <div className="mt-2">
            <div className="small text-muted">Preview</div>
            <img src={croppedPreview} alt="Cropped cover" style={{ width: 110, borderRadius: 4 }} />
          </div>
        )}
        {previousCovers.length > 0 && (
          <div className="mt-3">
            <div className="mb-1">Or use a previous cover for this book</div>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {previousCovers.map((cover) => (
                <button
                  type="button"
                  key={cover.key}
                  onClick={() => {
                    setSelectedPrevious(cover);
                    setImageToCrop(null);
                    setCroppedBlob(null);
                    setCroppedPreview(null);
                  }}
                  style={{
                    padding: 0,
                    border: selectedPrevious?.key === cover.key ? '3px solid #007bff' : '3px solid transparent',
                    borderRadius: 4,
                    background: 'none',
                  }}
                >
                  <img src={cover.url} alt="Previous cover" style={{ width: 73, display: 'block' }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={uploading}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={uploading || (!croppedBlob && !selectedPrevious)}>
          {uploading && <Spinner as="span" animation="border" size="sm" className="mr-1" />}
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
